import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Welle 9 (pre88): Document Picture-in-Picture für die Beraterseite.
 * Öffnet ein eigenes, immer im Vordergrund liegendes Fenster mit dem Partner-Videobild
 * (+ Mikro-/Auflegen-Steuerung), das sichtbar bleibt, wenn der Berater das Fenster wechselt.
 * Chromium-only (Chrome 116+); auf nicht unterstützten Browsern bleibt `supported=false`.
 *
 * Das PiP-Video ist stummgeschaltet — der Ton läuft weiter über das Audio-Element im
 * Hauptfenster (Hintergrund-Tabs behalten Audio). So gibt es keinen doppelten Ton.
 */
interface PipOptions {
  stream: MediaStream | null;
  micMuted: boolean;
  onToggleMic: () => void;
  onHangup: () => void;
  partnerLabel?: string;
  onSwap?: () => void;
  onScan?: () => void;
  onSignFile?: (file: File) => void;
}

const PIP_CSS = `
*{box-sizing:border-box;margin:0;padding:0}
body{background:#0a0a0a;color:#e8e8e8;font-family:'Rajdhani',system-ui,sans-serif;overflow:hidden}
.pipw{position:fixed;inset:0;display:flex;flex-direction:column;background:#0a0a0a}
.pipv{flex:1;width:100%;height:100%;object-fit:cover;background:#000}
.pipbar{display:flex;align-items:center;gap:6px;padding:7px 9px;background:rgba(10,10,10,.92);border-top:1px solid rgba(255,255,255,.08)}
.piplabel{flex:1;min-width:24px;font-size:12px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;opacity:.85}
.pipbtn{width:32px;height:32px;border-radius:8px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:#e8e8e8;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .12s}
.pipbtn:hover{background:rgba(255,255,255,.12)}
.pipbtn.muted{background:#dc2626;border-color:#dc2626;color:#fff}
.pipbtn.hang{background:#dc2626;border-color:#dc2626;color:#fff}
.pipbtn.hang:hover{background:#b91c1c}
.pipbtn svg{width:18px;height:18px;fill:currentColor}
`;

const MIC_ON = '<svg viewBox="0 0 24 24"><path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>';
const MIC_OFF = '<svg viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>';
const HANG = '<svg viewBox="0 0 24 24"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 0 0-2.66-1.85.998.998 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>';
const SWAP = '<svg viewBox="0 0 24 24"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 13c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 8.74A7.93 7.93 0 0 0 4 13c0 4.42 3.58 8 8 8v4l5-5-5-5v4z"/></svg>';
const SCAN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 13.2 L8.2 6.9 L7.5 5.5 L21 11.7 Z" fill="none"/><rect x="3" y="13.5" width="18" height="5" rx="1" fill="none"/><rect x="7.5" y="12" width="9" height="1.5" rx="0.3" fill="none"/><circle cx="20.2" cy="12.8" r="0.9" fill="currentColor" stroke="none"/></svg>';
const SIGN = '<svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';

export function useDocumentPip() {
  const supported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;
  const [active, setActive] = useState(false);
  const winRef = useRef<Window | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const micBtnRef = useRef<HTMLButtonElement | null>(null);

  const cleanup = useCallback(() => {
    winRef.current = null;
    videoRef.current = null;
    micBtnRef.current = null;
    setActive(false);
  }, []);

  const close = useCallback(() => {
    try { winRef.current?.close(); } catch { /* */ }
    cleanup();
  }, [cleanup]);

  const open = useCallback(async (opts: PipOptions) => {
    if (!supported) return;
    if (winRef.current) return; // bereits offen → kein erneutes Öffnen/Schließen
    try {
      const pip: Window = await (window as any).documentPictureInPicture.requestWindow({ width: 400, height: 280 });
      winRef.current = pip;

      const style = pip.document.createElement('style');
      style.textContent = PIP_CSS;
      pip.document.head.appendChild(style);

      const wrap = pip.document.createElement('div');
      wrap.className = 'pipw';

      const video = pip.document.createElement('video');
      video.className = 'pipv';
      video.autoplay = true;
      (video as any).playsInline = true;
      video.muted = true; // Ton läuft im Hauptfenster
      if (opts.stream) video.srcObject = opts.stream;
      videoRef.current = video;
      wrap.appendChild(video);

      const bar = pip.document.createElement('div');
      bar.className = 'pipbar';

      const label = pip.document.createElement('div');
      label.className = 'piplabel';
      label.textContent = opts.partnerLabel || 'Laufendes Gespräch';
      bar.appendChild(label);

      const mkBtn = (html: string, title: string, onClick: () => void) => {
        const b = pip.document.createElement('button');
        b.className = 'pipbtn';
        b.innerHTML = html;
        b.title = title;
        b.onclick = onClick;
        bar.appendChild(b);
        return b;
      };

      if (opts.onSwap) mkBtn(SWAP, 'Kamera wechseln', () => opts.onSwap!());
      if (opts.onScan) mkBtn(SCAN, 'Scan auslösen', () => opts.onScan!());
      if (opts.onSignFile) {
        const fileInput = pip.document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'application/pdf,.pdf';
        fileInput.style.display = 'none';
        fileInput.onchange = () => {
          const f = fileInput.files && fileInput.files[0];
          fileInput.value = '';
          if (f) opts.onSignFile!(f);
        };
        wrap.appendChild(fileInput);
        mkBtn(SIGN, 'Dokument signieren (PDF)', () => fileInput.click());
      }

      const micBtn = pip.document.createElement('button');
      micBtn.className = 'pipbtn' + (opts.micMuted ? ' muted' : '');
      micBtn.innerHTML = opts.micMuted ? MIC_OFF : MIC_ON;
      micBtn.title = 'Mikrofon';
      micBtn.onclick = () => opts.onToggleMic();
      micBtnRef.current = micBtn;
      bar.appendChild(micBtn);

      const hangBtn = pip.document.createElement('button');
      hangBtn.className = 'pipbtn hang';
      hangBtn.innerHTML = HANG;
      hangBtn.title = 'Auflegen';
      hangBtn.onclick = () => { opts.onHangup(); close(); };
      bar.appendChild(hangBtn);

      wrap.appendChild(bar);
      pip.document.body.appendChild(wrap);

      // Schließt der Nutzer das PiP-Fenster, räumen wir auf.
      pip.addEventListener('pagehide', () => cleanup(), { once: true });

      setActive(true);
    } catch (e) {
      console.warn('[pip] requestWindow fehlgeschlagen', e);
      cleanup();
    }
  }, [supported, close, cleanup]);

  /** Aktuellen Videostream im PiP aktualisieren (z. B. nach Kameratausch). */
  const setStream = useCallback((stream: MediaStream | null) => {
    const v = videoRef.current;
    if (v && stream && v.srcObject !== stream) v.srcObject = stream;
  }, []);

  /** Mikro-Status im PiP-Button spiegeln, wenn er im Hauptfenster geändert wird. */
  const syncMic = useCallback((muted: boolean) => {
    const b = micBtnRef.current;
    if (!b) return;
    b.className = 'pipbtn' + (muted ? ' muted' : '');
    b.innerHTML = muted ? MIC_OFF : MIC_ON;
  }, []);

  // Beim Unmount (Call-Ende/View-Wechsel) PiP schließen.
  useEffect(() => () => { try { winRef.current?.close(); } catch { /* */ } }, []);

  return { supported, active, open, close, setStream, syncMic };
}
